<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
	import { createPageSEO, createFAQSchema } from '$lib/config/seo';
	import { Button } from '$lib/components/ui/button';
	import * as Accordion from '$lib/components/ui/accordion/index';
	import {
		PUBLIC_EMAIL_NOREPLY,
		PUBLIC_EMAIL_SUPPORT,
		PUBLIC_EMAIL_FEEDBACK
	} from '$env/static/public';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	const faqs = [
		{
			question: 'How does TalentProfile work?',
			answer:
				'TalentProfile collects publicly available developer profiles from platforms like GitHub and LinkedIn. We analyze your skills, experience, and projects using AI, then match you with relevant job opportunities. When we find good matches, we send you personalized email alerts with positions that fit your profile.'
		},
		{
			question: "I didn't sign up. How did you get my information?",
			answer:
				"We collect publicly available information from developer platforms like GitHub, where you've made your profile visible to everyone. This is the same data that anyone on the internet can access. We use this public information to match you with relevant job opportunities. You can opt out or delete your data at any time using the preference link in our emails."
		},
		{
			question: 'What is a match score?',
			answer:
				'Our match score (0-100%) indicates how well a job aligns with your skills and experience. We analyze factors like your tech stack, programming languages, project history, seniority level, and location preferences. Higher scores mean better matches. We only send you opportunities with scores above 75% to ensure quality.'
		},
		{
			question: 'When will I receive my first email?',
			answer:
				"You'll receive your first email within 24-48 hours of us discovering your profile, provided we find job matches above our quality threshold (75%+). After that, you'll receive alerts based on your selected frequency (weekly or monthly) when new matching opportunities are posted."
		},
		{
			question: 'Is TalentProfile free?',
			answer:
				'Yes, TalentProfile is completely free for job seekers. We never charge developers for job matches or alerts. Our service is supported by partnerships with companies looking to hire talented developers.'
		},
		{
			question: 'What data do you collect about me?',
			answer:
				"We only collect publicly available information from your developer profiles, including: username, email (if public), skills and technologies, work experience, projects, location, and bio. We also track email interactions (opens and clicks) to improve our matching algorithm. We never collect private data or anything you haven't made publicly accessible."
		},
		{
			question: 'Do you share my data with employers?',
			answer:
				"No. We never share your personal information with employers or third parties. When you click a job link, you're directed to the company's website where you can apply directly. At that point, you're sharing information with them on your own terms. We only send you job matches—we don't send your profile to companies."
		},
		{
			question: 'How do I delete my data?',
			answer:
				'You can permanently delete all your data at any time: 1) Click the preference link in any email from us, 2) Scroll to the bottom and click "Delete My Data", 3) Confirm the deletion. This action is immediate and permanent. All your personal information will be removed from our system, and you\'ll never receive emails from us again.'
		},
		{
			question: 'Are you GDPR compliant?',
			answer:
				'Yes. We comply with GDPR and other privacy regulations. Our legal basis for processing is legitimate interest (matching candidates with opportunities) and the fact that we only process publicly available data. You have the right to access, correct, delete, or object to processing of your data at any time.'
		},
		{
			question: 'How often will I receive emails?',
			answer:
				"You can choose between weekly or monthly digests. Weekly digests are sent every Monday with jobs from the past week. Monthly digests are sent on the first Monday of each month. You'll only receive emails when we find high-quality matches (75%+ score). You can change your frequency anytime through the preference link in our emails."
		},
		{
			question: 'How do I update my preferences?',
			answer:
				"Every email from TalentProfile includes a unique preference link at the bottom. Click that link to change your email frequency (weekly or monthly), unsubscribe from all emails, or delete all your data permanently. This link is unique to you—don't share it with others."
		},
		{
			question: 'How do I unsubscribe?',
			answer:
				'Click the preference link at the bottom of any email, then click "Unsubscribe from All Emails." You\'ll stop receiving job alerts immediately. Your data remains in our system in case you want to resubscribe later. To permanently delete your data, use the "Delete My Data" option on the preferences page.'
		},
		{
			question: "Why aren't I getting more job matches?",
			answer:
				"We prioritize quality over quantity. We only send you jobs with a match score of 75% or higher to ensure relevance. If you're not receiving many matches, it could mean there are fewer jobs in your specific tech stack right now, your public profile lacks enough detail for accurate matching, or you're in a highly specialized niche with fewer postings. Try updating your GitHub profile with more projects, skills, and descriptions to improve matching."
		},
		{
			question: "The jobs don't match my interests. Why?",
			answer: `Our algorithm matches based on your public profile data. If matches seem off, consider updating your GitHub bio to better reflect your interests, adding more recent projects in your preferred technologies, and ensuring your public profile accurately represents your current skills. We're constantly improving our matching algorithm. Your feedback helps—let us know at ${PUBLIC_EMAIL_FEEDBACK}`
		},
		{
			question: 'How do I apply to jobs?',
			answer:
				"Click the job link in your email to be directed to the company's website or job board where the position is posted. From there, follow the company's application process. TalentProfile is just a matching service—we don't handle applications or hiring."
		}
	];

	const seo = createPageSEO({
		title: 'Frequently Asked Questions - TalentProfile',
		description:
			'Find answers to common questions about TalentProfile. How it works, privacy, pricing, and more.',
		canonical: '/faq',
		structuredData: createFAQSchema(faqs)
	});
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white to-gray-50">
	<!-- Content -->
	<div class="container mx-auto px-4 py-20">
		<div class="mx-auto max-w-4xl space-y-8">
			<!-- Hero -->
			<div class="mb-16 text-center">
				<h1 class="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">
					Frequently Asked Questions
				</h1>
				<p class="text-lg text-gray-600">Everything you need to know about TalentProfile</p>
			</div>

			<!-- Quick Links -->
			<div class="mb-12 grid grid-cols-1 gap-4 md:grid-cols-3">
				<a
					href="#getting-started"
					class="rounded-lg border bg-white p-4 transition-colors hover:border-blue-600"
				>
					<div class="mb-2 text-blue-600">
						<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 10V3L4 14h7v7l9-11h-7z"
							/>
						</svg>
					</div>
					<div class="font-semibold text-gray-900">Getting Started</div>
					<div class="text-sm text-gray-600">How it works</div>
				</a>
				<a
					href="#privacy-security"
					class="rounded-lg border bg-white p-4 transition-colors hover:border-blue-600"
				>
					<div class="mb-2 text-blue-600">
						<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
							/>
						</svg>
					</div>
					<div class="font-semibold text-gray-900">Privacy & Security</div>
					<div class="text-sm text-gray-600">Your data</div>
				</a>
				<a
					href="#managing-alerts"
					class="rounded-lg border bg-white p-4 transition-colors hover:border-blue-600"
				>
					<div class="mb-2 text-blue-600">
						<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
							/>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
							/>
						</svg>
					</div>
					<div class="font-semibold text-gray-900">Managing Alerts</div>
					<div class="text-sm text-gray-600">Preferences</div>
				</a>
			</div>

			<!-- FAQ Sections -->
			<div class="space-y-12">
				<!-- Getting Started -->
				<section id="getting-started">
					<h2 class="mb-6 text-2xl font-bold text-gray-900 md:text-3xl">Getting Started</h2>

					<Accordion.Root type="multiple" class="space-y-4">
						<Accordion.Item
							value="how-it-works"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								How does TalentProfile work?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								TalentProfile collects publicly available developer profiles from platforms like
								GitHub and LinkedIn. We analyze your skills, experience, and projects using AI, then
								match you with relevant job opportunities. When we find good matches, we send you
								personalized email alerts with positions that fit your profile.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="no-signup" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								I didn't sign up. How did you get my information?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								We collect publicly available information from developer platforms like GitHub,
								where you've made your profile visible to everyone. This is the same data that
								anyone on the internet can access. We use this public information to match you with
								relevant job opportunities. You can opt out or delete your data at any time using
								the preference link in our emails.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="match-score"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								What is a match score?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Our match score (0-100%) indicates how well a job aligns with your skills and
								experience. We analyze factors like your tech stack, programming languages, project
								history, seniority level, and location preferences. Higher scores mean better
								matches. We only send you opportunities with scores above 75% to ensure quality.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="first-email"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								When will I receive my first email?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								You'll receive your first email within 24-48 hours of us discovering your profile,
								provided we find job matches above our quality threshold (75%+). After that, you'll
								receive alerts based on your selected frequency (weekly or monthly) when new
								matching opportunities are posted.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="cost" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								Is TalentProfile free?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Yes, TalentProfile is completely free for job seekers. We never charge developers
								for job matches or alerts. Our service is supported by partnerships with companies
								looking to hire talented developers.
							</Accordion.Content>
						</Accordion.Item>
					</Accordion.Root>
				</section>

				<!-- Privacy & Security -->
				<section id="privacy-security">
					<h2 class="mb-6 text-2xl font-bold text-gray-900 md:text-3xl">Privacy & Security</h2>

					<Accordion.Root type="multiple" class="space-y-4">
						<Accordion.Item
							value="data-collected"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								What data do you collect about me?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								We only collect publicly available information from your developer profiles,
								including: username, email (if public), skills and technologies, work experience,
								projects, location, and bio. We also track email interactions (opens and clicks) to
								improve our matching algorithm. We never collect private data or anything you
								haven't made publicly accessible.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="data-sharing"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								Do you share my data with employers?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								<strong>No.</strong> We never share your personal information with employers or third
								parties. When you click a job link, you're directed to the company's website where you
								can apply directly. At that point, you're sharing information with them on your own terms.
								We only send you job matches—we don't send your profile to companies.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="delete-data"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								How do I delete my data?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								You can permanently delete all your data at any time:
								<ol class="mt-3 list-inside list-decimal space-y-1">
									<li>Click the preference link in any email from us</li>
									<li>Scroll to the bottom and click "Delete My Data"</li>
									<li>Confirm the deletion</li>
								</ol>
								<p class="mt-3">
									This action is immediate and permanent. All your personal information will be
									removed from our system, and you'll never receive emails from us again.
								</p>
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="gdpr" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								Are you GDPR compliant?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Yes. We comply with GDPR and other privacy regulations. Our legal basis for
								processing is legitimate interest (matching candidates with opportunities) and the
								fact that we only process publicly available data. You have the right to access,
								correct, delete, or object to processing of your data at any time. See our
								<a href="/privacy" class="text-blue-600 hover:underline">Privacy Policy</a> for details.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="security" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								How secure is my data?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								We use industry-standard security measures including encrypted data transmission
								(HTTPS/TLS), secure database storage, access controls, and regular security audits.
								Your data is stored in secure, access-controlled databases and is only accessible to
								authorized team members who need it to operate the service.
							</Accordion.Content>
						</Accordion.Item>
					</Accordion.Root>
				</section>

				<!-- Managing Alerts -->
				<section id="managing-alerts">
					<h2 class="mb-6 text-2xl font-bold text-gray-900 md:text-3xl">Managing Your Alerts</h2>

					<Accordion.Root type="multiple" class="space-y-4">
						<Accordion.Item value="frequency" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								How often will I receive emails?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								You can choose between weekly or monthly digests. Weekly digests are sent every
								Monday with jobs from the past week. Monthly digests are sent on the first Monday of
								each month. You'll only receive emails when we find high-quality matches (75%+
								score). You can change your frequency anytime through the preference link in our
								emails.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="preferences"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								How do I update my preferences?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Every email from TalentProfile includes a unique preference link at the bottom.
								Click that link to:
								<ul class="mt-3 list-inside list-disc space-y-1">
									<li>Change your email frequency (weekly or monthly)</li>
									<li>Unsubscribe from all emails</li>
									<li>Delete all your data permanently</li>
								</ul>
								<p class="mt-3">This link is unique to you—don't share it with others.</p>
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="unsubscribe"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								How do I unsubscribe?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Click the preference link at the bottom of any email, then click "Unsubscribe from
								All Emails." You'll stop receiving job alerts immediately. Your data remains in our
								system in case you want to resubscribe later. To permanently delete your data, use
								the "Delete My Data" option on the preferences page.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="resubscribe"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								Can I resubscribe after unsubscribing?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Yes, if you unsubscribed but didn't delete your data, you can resubscribe anytime
								using the same preference link. Just click "Resubscribe to Job Alerts" on the
								preferences page. However, if you deleted your data, we cannot restore it or
								resubscribe you.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="spam" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								Why are your emails going to spam?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								If our emails are landing in spam, try these steps:
								<ul class="mt-3 list-inside list-disc space-y-1">
									<li>Mark our email as "Not Spam" in your inbox</li>
									<li>Add {PUBLIC_EMAIL_NOREPLY} to your contacts</li>
									<li>Check your spam filters and whitelist our domain</li>
								</ul>
								<p class="mt-3">
									If issues persist, contact us at
									<a href="mailto:{PUBLIC_EMAIL_SUPPORT}" class="text-blue-600 hover:underline"
										>{PUBLIC_EMAIL_SUPPORT}</a
									>
								</p>
							</Accordion.Content>
						</Accordion.Item>
					</Accordion.Root>
				</section>

				<!-- Job Matches -->
				<section id="job-matches">
					<h2 class="mb-6 text-2xl font-bold text-gray-900 md:text-3xl">Job Matches</h2>

					<Accordion.Root type="multiple" class="space-y-4">
						<Accordion.Item value="quality" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								Why aren't I getting more job matches?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								We prioritize quality over quantity. We only send you jobs with a match score of 75%
								or higher to ensure relevance. If you're not receiving many matches, it could mean:
								<ul class="mt-3 list-inside list-disc space-y-1">
									<li>There are fewer jobs in your specific tech stack right now</li>
									<li>Your public profile lacks enough detail for accurate matching</li>
									<li>You're in a highly specialized niche with fewer postings</li>
								</ul>
								<p class="mt-3">
									Try updating your GitHub profile with more projects, skills, and descriptions to
									improve matching.
								</p>
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="wrong-matches"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								The jobs don't match my interests. Why?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Our algorithm matches based on your public profile data. If matches seem off,
								consider:
								<ul class="mt-3 list-inside list-disc space-y-1">
									<li>Updating your GitHub bio to better reflect your interests</li>
									<li>Adding more recent projects in your preferred technologies</li>
									<li>Ensuring your public profile accurately represents your current skills</li>
								</ul>
								<p class="mt-3">
									We're constantly improving our matching algorithm. Your feedback helps—let us know
									at
									<a href="mailto:{PUBLIC_EMAIL_FEEDBACK}" class="text-blue-600 hover:underline"
										>{PUBLIC_EMAIL_FEEDBACK}</a
									>
								</p>
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item
							value="expired-jobs"
							class="rounded-lg border bg-white px-6 last:border-b"
						>
							<Accordion.Trigger class="text-left font-semibold">
								Some job links don't work. Why?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Job postings can be removed by employers at any time. We scan for new jobs daily,
								but companies may fill positions quickly. If a link is broken, the job was likely
								filled or removed. This is normal in fast-moving tech markets. We recommend applying
								quickly to jobs that interest you.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="apply" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								How do I apply to jobs?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Click the job link in your email to be directed to the company's website or job
								board where the position is posted. From there, follow the company's application
								process. TalentProfile is just a matching service—we don't handle applications or
								hiring.
							</Accordion.Content>
						</Accordion.Item>

						<Accordion.Item value="tracking" class="rounded-lg border bg-white px-6 last:border-b">
							<Accordion.Trigger class="text-left font-semibold">
								Do you track which jobs I click?
							</Accordion.Trigger>
							<Accordion.Content class="leading-relaxed text-gray-600">
								Yes, we track email opens and job clicks to improve our matching algorithm. This
								helps us understand which types of jobs are most relevant to different developers.
								We use this anonymized data to make better matches for everyone. We never share your
								individual click data with employers.
							</Accordion.Content>
						</Accordion.Item>
					</Accordion.Root>
				</section>
			</div>

			<GlopayCtaHorizontal audience="talent" placement="faq-post-faq" />

			<!-- Still Have Questions CTA -->
			<div
				class="mt-16 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-12 text-center text-white"
			>
				<h2 class="mb-4 text-2xl font-bold md:text-3xl">Still Have Questions?</h2>
				<p class="mb-6 opacity-90">
					Can't find the answer you're looking for? Our support team is here to help.
				</p>
				<Button size="lg" variant="secondary" onclick={() => (window.location.href = '/contact')}>
					Contact Support
				</Button>
			</div>
		</div>
	</div>
</div>
